import {cva} from 'platform/shared';

import {CSSProperties, ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {CSSGridTrackSize} from '../../types/CSSGridTrackSize';
import {FlexboxAlign} from '../../types/FlexboxAlign';
import {GridAutoFlow} from '../../types/GridAutoFlow';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';
import {getSize} from '../../utils/getSize';

const gridVariants = cva('grid');

export interface GridProps extends TestIdProps {
  columns: number | ValueByDevice<number>;
  spacing?: Integer | ValueByDevice<Integer>;
  grow?: number;
  verticalSpacing?: Integer | ValueByDevice<Integer>;
  horizontalSpacing?: Integer | ValueByDevice<Integer>;
  children?: ReactNode;
  minHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  align?: FlexboxAlign;
  autoRows?: CSSGridTrackSize | ValueByDevice<CSSGridTrackSize>;
  autoFlow?: GridAutoFlow;
}

export function Grid(props: GridProps) {
  const columns = useResponsivePropValue(props.columns);
  const spacing = useResponsivePropValue(props.spacing);
  const verticalSpacing = useResponsivePropValue(props.verticalSpacing);
  const horizontalSpacing = useResponsivePropValue(props.horizontalSpacing);
  const minHeight = useResponsivePropValue(props.minHeight);
  const autoRows = useResponsivePropValue(props.autoRows);

  const verticalGap = verticalSpacing ?? spacing ?? DEFAULT_GAP;
  const horizontalGap = horizontalSpacing ?? spacing ?? DEFAULT_GAP;
  const style: CSSProperties = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gridAutoRows: autoRows ?? 'initial',
    gridAutoFlow: props.autoFlow ?? 'initial',
    alignItems: props.align ?? 'initial',
    flexGrow: props.grow ?? 'initial',
    gap: `${getSize(verticalGap)} ${getSize(horizontalGap)}`,
    minWidth: 0,
    minHeight: isNotNil(minHeight) ? getCssSize(minHeight) : 0,
  };

  return (
    <div data-testid={props['data-testid']} className={gridVariants()} style={style}>
      {props.children}
    </div>
  );
}

const DEFAULT_GAP = 4;
